import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, concatMap, map } from 'rxjs';

import { H5pContentService } from '@services/h5p-content.service';
import { Member } from '@models/member.model';
import { environment } from '@environment';
import {
  CourseOutline,
  BulkCreateResponse,
  buildBulkCreatePayload,
  SkeletonRequestPayload,
  SkeletonResponse,
  SectionsContentRequestPayload,
  SectionsContentResponse,
} from '@core/constants/guided-course-creation.constants';

export interface GuidedCourseCreationInput {
  title: string;
  language: string;
  estimatedTimeInMinutes: number;
  outline: CourseOutline;
  author: Member;
}

interface CreateFromOutlineActivity {
  h5p_library: string;
  content_id: string;
  content_json: string;
  order: number;
  ai_generated: boolean;
}

interface CreateFromOutlineSection {
  title: string;
  order: number;
  activities: CreateFromOutlineActivity[];
}

interface CreateFromOutlinePayload {
  course: {
    name: string;
    language: string;
    estimated_time_in_minutes: number;
    author_id: number;
    sections: CreateFromOutlineSection[];
  };
}

interface CreateFromOutlineResponse {
  data: { id: string };
}

@Injectable({ providedIn: 'root' })
export class GuidedCourseCreationService {
  private readonly http = inject(HttpClient);
  private readonly h5pContentService = inject(H5pContentService);

  generateSkeleton(
    payload: SkeletonRequestPayload,
  ): Observable<SkeletonResponse> {
    return this.http.post<SkeletonResponse>(
      `${environment.backendBaseUrl}/api/v1/ai/course_outlines/skeleton`,
      payload,
    );
  }

  generateSectionsContent(
    payload: SectionsContentRequestPayload,
  ): Observable<SectionsContentResponse> {
    return this.http.post<SectionsContentResponse>(
      `${environment.backendBaseUrl}/api/v1/ai/course_outlines/sections_content`,
      payload,
    );
  }

  create(input: GuidedCourseCreationInput): Observable<{ id: string }> {
    const bulkPayload = buildBulkCreatePayload(input.outline, input.language);

    return this.h5pContentService
      .bulkCreate(bulkPayload)
      .pipe(
        concatMap(bulkResult => this.callCreateFromOutline(input, bulkResult)),
      );
  }

  private callCreateFromOutline(
    input: GuidedCourseCreationInput,
    bulkResult: BulkCreateResponse,
  ): Observable<{ id: string }> {
    const payload: CreateFromOutlinePayload = {
      course: {
        name: input.title,
        language: input.language,
        estimated_time_in_minutes: input.estimatedTimeInMinutes,
        author_id: Number(input.author.id),
        sections: input.outline.sections.map((section, sectionIndex) => {
          const bulkSection = bulkResult.sections[sectionIndex];
          return {
            title: section.headline,
            order: sectionIndex,
            activities: section.modules.map((_, moduleIndex) => {
              const bulkModule = bulkSection?.modules[moduleIndex];
              return {
                h5p_library: bulkModule?.h5p_library ?? '',
                content_id: bulkModule?.content_id ?? '',
                content_json: bulkModule?.content_json ?? '{}',
                order: moduleIndex,
                ai_generated: true,
              };
            }),
          };
        }),
      },
    };

    return this.http
      .post<CreateFromOutlineResponse>(
        `${environment.backendBaseUrl}/api/v1/courses/create_from_outline`,
        payload,
        { withCredentials: true },
      )
      .pipe(map(response => ({ id: response.data.id })));
  }
}
